import { Injectable, inject } from '@angular/core';
import { HttpService } from './http.service';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { LoginResponse } from '../models/user.model';
import { Router } from '@angular/router';
import { EruqlConfig, DataSources, QueryPayload, SqlQueryPayload, GraphqlQueryPayload, DbSecurityRules, TransformRules } from '../models/eruql.model';

@Injectable({
  providedIn: 'root'
})
export class EruqlService {
  private router = inject(Router);
  
  constructor(private httpService: HttpService) { }
  
  fetchConfig(url: string, project_name: string): Observable<{ project: EruqlConfig }> {
    return this.httpService.get<any>(
      `${url}/store/${project_name}/config`
    ).pipe(
      map(response => {
        if (response && response.project) {
          return { project: response.project };
        } else {
          return { project: {} };
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Projects not found'));
      })
    );
  }

  addJoin(url: string, project_name: string, dbAlias: string, payload: any): Observable<any> {
    return this.httpService.post<any>(
      `${url}/store/${project_name}/datasource/schema/${dbAlias}/addjoin`,
      payload
    );
  }

  // Backend route is DELETE with the TableJoins json as body.
  removeJoin(url: string, project_name: string, dbAlias: string, payload: any): Observable<any> {
    return this.httpService.delete<any>(
      `${url}/store/${project_name}/datasource/schema/${dbAlias}/removejoin`,
      { body: payload }
    );
  }

  // Table names travel with '.' encoded as '___' (the handler reverses it).
  private tablePath(tableName: string): string {
    return encodeURIComponent(tableName.replace('.', '___'));
  }

  saveTableSecurity(url: string, project_name: string, dbAlias: string, tableName: string, rules: DbSecurityRules): Observable<any> {
    return this.httpService.post<any>(
      `${url}/store/${project_name}/datasource/schema/${dbAlias}/securetable/${this.tablePath(tableName)}`,
      rules
    ).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => new Error(error.error?.error || 'Failed to save table security')))
    );
  }

  saveTableTransformation(url: string, project_name: string, dbAlias: string, tableName: string, rules: TransformRules): Observable<any> {
    return this.httpService.post<any>(
      `${url}/store/${project_name}/datasource/schema/${dbAlias}/transformtable/${this.tablePath(tableName)}`,
      rules
    ).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => new Error(error.error?.error || 'Failed to save table transformation')))
    );
  }

  
  fetchDbDetails(url: string, project_name: string,db_name:string): Observable<any> {
    return this.httpService.get<any>(
      `${url}/store/${project_name}/datasource/schema/${db_name}`
    ).pipe(
      map(response => {
        if (response && (response.SchemaTables || response.OtherTables)) {
          return response;
        } else {
          console.warn('Invalid response structure:', response);
          return {};
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to fetch database details'));
      })
    );
  }

  createTable(url: string, project_name: string, payload: any, dbAlias: string, pathNameOverride?: string): Observable<any> {
    console.log('Creating table payload:', payload);
    // Get the first column object which contains the table info
    const firstColumn = Object.values(payload)[0] as {
      tbl_name: string;
      tbl_Schema: string;
      col_name: string;
    };
    const pathName = (pathNameOverride || firstColumn.tbl_name || '').trim();
    console.log('savetable path segment:', pathName, 'overrideProvided:', !!pathNameOverride);
    return this.httpService.post<any>(
      `${url}/store/${project_name}/datasource/schema/${dbAlias}/savetable/${pathName}`,
      payload
    );
  }

  dropTable(url:string,project_name:string,dbAlias:string,tableName:string):Observable<any>{
    return this.httpService.delete<any>(
      `${url}/store/${project_name}/datasource/schema/${dbAlias}/droptable/${tableName}`
    );
  }

  addSchema(url:string,project_name:string,schemaName:string,dbName:string):Observable<any>{
    return this.httpService.post<any>(
      `${url}/store/${project_name}/datasource/schema/${dbName}/addtable/${schemaName}`,
      {}
    ).pipe(
      tap((response) => console.log('Schema added successfully:', response)),
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(
          () =>
            new Error(
              error.error?.error || 'Failed to add schema'
            )
        );
      })
    );
  }

  saveQuery(url: string, project_name: string, query: QueryPayload, query_name: string, queryType: 'sql' | 'graphql'): Observable<any> {
    const endpoint = queryType;
    return this.httpService.post<any>(
      `${url}/store/${project_name}/myquery/save/${query_name}/${endpoint}`,
      query
    );
  }

  deleteQuery(url: string, project_name: string, query_name: string): Observable<any> {
    return this.httpService.delete<any>(
      `${url}/store/${project_name}/myquery/remove/${query_name}`
    );
  }
  
  // Alternative approach using type guards
  private isSqlQuery(query: QueryPayload): query is SqlQueryPayload {
    // You could add a discriminator property or check for specific properties
    // For now, we'll rely on the queryType parameter passed from component
    return !('operationType' in query);
  }
  
  private isGraphqlQuery(query: QueryPayload): query is GraphqlQueryPayload {
    return 'operationType' in query;
  }
  
  // Alternative saveQuery method using type detection
  saveQueryWithTypeDetection(url: string, project_name: string, query: QueryPayload, query_name: string): Observable<any> {
    const endpoint = this.isGraphqlQuery(query) ? 'graphql' : 'sql';
    return this.httpService.post<any>(
      `${url}/store/${project_name}/myquery/save/${query_name}/${endpoint}`,
      query
    );
  }


  removeSchema(url:string,project_name:string,schemaName:string,dbName:string):Observable<any>{
    return this.httpService.delete<any>(
      `${url}/store/${project_name}/datasource/schema/${dbName}/removetable/${schemaName}`,
    )
  }

  saveDb(url: string, project_name: string, dbform: DataSources): Observable<any> {
    return this.httpService.post<any>(
      `${url}/store/${project_name}/datasource/save/${dbform.db_alias}`,
      dbform
    ).pipe(
      tap(response => console.log('Database saved Successfully:', response)),
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to save database'));
      })
    );
  }

  fetchDatasourceConfig(url: string, project_name: string, dbAlias: string): Observable<DataSources> {
    return this.httpService.get<{ datasource: DataSources }>(
      `${url}/store/${project_name}/datasource/config/${dbAlias}`
    ).pipe(
      map(response => response.datasource),
      catchError((error: HttpErrorResponse) => throwError(() => new Error(error.error?.error || 'Failed to fetch datasource')))
    );
  }

  // datasource/save keeps the stored db_security_rules of an existing datasource; this route replaces them.
  saveDbSecurityRules(url: string, project_name: string, dbAlias: string, rules: DbSecurityRules): Observable<any> {
    return this.httpService.post<any>(
      `${url}/store/${project_name}/datasource/dbsecurityrules/${dbAlias}`,
      rules
    ).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => new Error(error.error?.error || 'Failed to save security rules')))
    );
  }

  deleteDb(url: string, project_name: string, dbform: DataSources): Observable<any> {
    return this.httpService.delete<any>(
      `${url}/store/${project_name}/datasource/remove/${dbform.db_alias}`,
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to delete database'));
      })
    );
  }

  
  
  loginWithUserPassword(url: string, username: string, password: string): Observable<LoginResponse> {
    return this.httpService.post<LoginResponse>(
      url,
      { username, password }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Login failed'));
      })
    );
  }
}